import { ref, computed, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment-timezone";
import store from "@/store";
import ability from "@/libs/acl/ability";

import { debounce } from "lodash";

export default function useList() {
  const refListTable = ref(null);
  const toast = useToast();
  const tenantFilter = ref(null);
  let tableColumns = [
    { label: "id", key: "id", sortable: false },
    { label: "unit_deactivation_catalog.name", key: "name", sortable: false },
    {
      label: "unit.description",
      key: "description",
      sortable: false,
      thStyle: "min-width: 400px",
    },
    {
      label: "tenants.title",
      key: "tenants",
      sortable: false,
      formatter: (value, key, item) => {
        return value
          ? value
              .map((tenant) => {
                return tenant.name;
              })
              .join(", ")
          : "";
      },
      thStyle: "min-width: 200px",
    },
    {
      label: "date.created_at",
      key: "created_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
    {
      label: "date.updated_at",
      key: "updated_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
  ];

  if (
    ability.can("update", "UnitDeactivationCatalog") == true ||
    ability.can("delete", "UnitDeactivationCatalog") == true
  ) {
    tableColumns.splice(0, 0, { label: "actions.name", key: "actions" });
  }

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const fetchList = (ctx, callback) => {
    store
      .dispatch("unit_deactivation_catalog/getAll", {
        q: searchQuery.value,
        max: perPage.value,
        "page[n]": currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        id_tenant: tenantFilter.value,
      })
      .then((response) => {
        callback(response.data);
        totalList.value = response.meta.total;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error obteniendo la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const formatDateAssigned = (value) => {
    const formattedDate = moment(value).format("DD/MM/YYYY HH:mm:ss");
    return formattedDate;
  };

  const refetchData = () => {
    refListTable.value.refresh();
  };

  watch([currentPage, perPage], () => {
    refetchData();
  });

  watch(
    [searchQuery],
    debounce(() => {
      refetchData();
    }, 500)
  );

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      id_tenant: tenantFilter.value,
    };
    refetchData();
  };
  const clearSearchFilters = () => {
    tenantFilter.value = null;
    buildFilters();
  };
  // ============================================

  return {
    fetchList,
    tableColumns,
    perPage,
    currentPage,
    totalList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    refListTable,
    isSortDirDesc,
    refetchData,
    formatDateAssigned,
    tenantFilter,
    filtersCount,
    buildFilters,
    clearSearchFilters,
  };
}

function useFilterCounter(filters) {
  return computed(() => {
    let count = 0;
    Object.keys(filters.value).forEach((key) => {
      if (filters.value[key] != null && filters.value[key] != "" && filters.value[key] != undefined) {
        if (Array.isArray(filters.value[key])) {
          if (filters.value[key].length > 0) count++;
        } else {
          count++;
        }
      }
    });
    return count;
  });
}

